import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useCurrency } from "../../common/hooks/useCurrency";
import DeleteModal from "../DeleteModal/DeleteModal";
import { Box, Button, CopyableBox, FlexColBox } from "../../common/components";
import { ComboItems, ItemPricing, OrderDetail } from "../../types";
import { AdminOrderContext } from "../../context/Admin/Order";
import { useMoment } from "../../common/hooks/useMoment";
import { palette } from "../../styles/theme";
import { extraSizeList } from "../../constant/common.constant";
import OrderItemSizes from "./OrderItemSizes";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../common/hooks/useAuth";

interface Props {
  title: string;
  item: ItemPricing;
  comboItems: ComboItems;
  showDistributorPrices?: boolean;
  orderDate?: Date;
  preorderDetail?: OrderDetail;
}

const OrderItemDetails = ({
  title,
  item,
  comboItems,
  showDistributorPrices,
  orderDate,
  preorderDetail,
}: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFromAdmin = location.pathname.includes("/admin");
  const { isAdmin } = useAuth();
  const { onEditItemDesign, onUpdateSizesQty, onRemoveDesign } =
    useContext(AdminOrderContext);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { weeksFromDate } = useMoment();

  const onDeleteClickClose = () => {
    setShowDeleteModal(false);
  };

  const { getCurrencyFormatter } = useCurrency();
  const currency = comboItems.currency;
  const currencyFormatter = getCurrencyFormatter(currency);

  const onClickEditDesign = useCallback(() => {
    return onEditItemDesign?.(item.designId);
  }, [item.designId, onEditItemDesign]);

  const onClicRemovePurchasedItem = useCallback(() => {
    return onRemoveDesign?.(item.designId);
  }, [item.designId, onRemoveDesign]);

  const basePrice = showDistributorPrices
    ? item.distributor?.itemPriceBreakdown.basePrice
    : item.itemPriceBreakdown.basePrice;

  const pricePerUnit = showDistributorPrices
    ? item.distributor?.pricePerUnit
    : item.pricePerUnit;

  const hidePrice = useMemo(
    () => preorderDetail?.pricing.quoteRequest && !isAdmin,
    [preorderDetail?.pricing.quoteRequest, isAdmin]
  );

  const priceForExtraCredits = showDistributorPrices
    ? item.distributor?.itemPriceBreakdown.priceForExtraCredits
    : item.itemPriceBreakdown.priceForExtraCredits;

  const shippingPerUnit = showDistributorPrices
    ? item.distributor?.shippingPerUnit
    : item.shippingPerUnit;

  const shippingTotal =
    showDistributorPrices && item.distributor
      ? item.distributor?.shippingTotal
      : item.shippingTotal;

  const extraSizeTotal =
    showDistributorPrices && item.distributor
      ? item.distributor?.extraSizeTotal
      : item.extraSizeTotal;

  const extraSizeUnitPrice =
    showDistributorPrices && item.distributor
      ? item.distributor?.pricePerExtraSize
      : item.pricePerExtraSize;

  const shippingOptions = item.shippingOptions?.find(
    (option) => option.tier === item.selectedShippingTier
  );

  const shouldHideDelayAndExpress = useMemo(() => {
    return item.shippingOptions?.some(
      (option) => item.quantity >= option.hideDelayAfter
    );
  }, [item.quantity, item.shippingOptions]);

  const shippingOptionsString = useMemo(() => {
    return t(
      shouldHideDelayAndExpress
        ? "order.shipping_options_without_delay"
        : "order.shipping_options",
      {
        shippingTier: t(`general.shipping_tier.${item.selectedShippingTier}`),
        startDate: weeksFromDate(shippingOptions?.minWeeks, orderDate),
        endDate: weeksFromDate(shippingOptions?.maxWeeks, orderDate),
      }
    );
  }, [
    shouldHideDelayAndExpress,
    item.selectedShippingTier,
    shippingOptions,
    orderDate,
    weeksFromDate,
    t,
  ]);

  const shippingPriceString = useMemo(() => {
    return hidePrice
      ? t("order.tbd")
      : `${currencyFormatter?.format(shippingTotal)} (${item.quantity} x ${currencyFormatter?.format(shippingPerUnit ?? 0)})`;
  }, [
    shippingTotal,
    item.quantity,
    shippingPerUnit,
    currencyFormatter,
    t,
    hidePrice,
  ]);

  const extraSizeBreakdown = useMemo(() => {
    const breakdownArray = item.sizes
      .filter(
        (size) =>
          extraSizeList.includes(size.name) && size.qty && Number(size.qty) > 0
      )
      .map((size) => {
        return `${t(`sizes.${size.name.toUpperCase()}`)} x ${size.qty}`;
      });
    return breakdownArray.join(", ");
  }, [item.sizes, t]);

  return (
    <Box
      p="24px"
      border="1px solid"
      borderColor={palette.grey.lightGrey}
      borderRadius="8px"
      display="flex"
      flexDirection="column"
      gap="32px"
      sx={{
        backgroundColor: palette.white,
      }}
    >
      <Box
        display="flex"
        gap="24px"
        flexDirection={{ xs: "column", lg: "row" }}
      >
        <FlexColBox
          gap="24px"
          px="24px"
          display="flex"
          flexDirection={{ xs: "row", lg: "column" }}
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          sx={{
            "& > img": {
              width: "100%",
              maxWidth: "130px",
              maxHeight: "160px",
              objectFit: "contain",
            },
          }}
        >
          {item.productUrls?.map((image) => (
            <img src={image} alt={image} key={image} />
          ))}
          <Box display="flex" gap="8px" sx={{ displayPrint: "none" }}>
            {!!onEditItemDesign && (
              <Button
                onClick={onClickEditDesign}
                size="small"
                variant="outlined"
                sx={{
                  width: "fit-content",
                  minWidth: "fit-content",
                }}
              >
                {t("general.edit")}
              </Button>
            )}
            {!!onRemoveDesign && (
              <Button
                onClick={() => {
                  setShowDeleteModal(true);
                }}
                size="small"
                variant="outlined"
                sx={{
                  width: "fit-content",
                  minWidth: "fit-content",
                }}
              >
                {t("general.remove")}
              </Button>
            )}
          </Box>
        </FlexColBox>
        <FlexColBox gap="32px" fullWidth>
          <FlexColBox gap="24px" fullWidth>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "medium",
                color: palette.grey.nearBlack,
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                border: `1px solid ${palette.grey.lightGrey}`,
                borderRadius: "8px",
                overflowX: "auto",
              }}
            >
              <Table
                sx={{
                  minWidth: "400px",
                  width: "100%",
                  "& .MuiTableCell-root": {
                    border: "none",
                  },
                }}
              >
                <TableBody
                  sx={{
                    "& tr:first-of-type td": {
                      borderBottom: "none",
                    },
                    "& tr:last-of-type td": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableRow
                    sx={{
                      backgroundColor: palette.grey.lightBackground,
                    }}
                  >
                    <TableCell width="40%">
                      <Typography variant="textSmBold">
                        {t(`order.product_and_description`)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="textSmBold">
                        {t(`order.quantity`)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="textSmBold">
                        {t(`order.unit_price`)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="textSmBold">
                        {t(`order.total`)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      "& > td": {
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <CopyableBox
                          value={item.productName}
                          hideCopyButton={!isFromAdmin}
                        >
                          <Typography variant="textSmBold">
                            {t(`${item.productName}.name`)}
                          </Typography>
                        </CopyableBox>
                        <CopyableBox
                          value={t(`order.total_credits`, {
                            count: item.itemPriceBreakdown.numberOfCredits ?? 0,
                            subText: t(`order.extra_credits`, {
                              count: item.itemPriceBreakdown.extraCredits ?? 0,
                            }),
                          })}
                          hideCopyButton={!isFromAdmin}
                        >
                          <Typography
                            variant="textXs"
                            color={palette.grey.primary}
                          >
                            {t(`order.total_credits`, {
                              count:
                                item.itemPriceBreakdown.numberOfCredits ?? 0,
                              subText: t(`order.extra_credits`, {
                                count:
                                  item.itemPriceBreakdown.extraCredits ?? 0,
                              }),
                            })}
                          </Typography>
                        </CopyableBox>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="textSmBold">
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <Typography variant="textSmBold">
                          {hidePrice
                            ? t("order.tbd")
                            : pricePerUnit &&
                              currencyFormatter?.format(pricePerUnit)}
                        </Typography>
                        <Typography
                          variant="textXs"
                          color={palette.grey.primary}
                        >
                          {hidePrice
                            ? t("order.tbd")
                            : `${basePrice && currencyFormatter?.format(basePrice)} + ${currencyFormatter?.format(priceForExtraCredits ?? 0)}`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="textSmBold">
                        {hidePrice
                          ? t("order.tbd")
                          : pricePerUnit &&
                            currencyFormatter?.format(
                              pricePerUnit * item.quantity
                            )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* extra sizes */}
                  {item.numberOfExtraSize > 0 && (
                    <>
                      <TableRow
                        sx={{
                          "& > td": {
                            verticalAlign: "top",
                          },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <Typography variant="textSmBold">
                              {t(`order.extra_sizes`)}
                            </Typography>
                            <CopyableBox
                              value={t(`order.extra_size_breakdown`, {
                                productName: t(`${item.productName}.name`),
                                breakdown: extraSizeBreakdown,
                              })}
                              hideCopyButton={!isFromAdmin}
                            >
                              <Typography
                                variant="textXs"
                                color={palette.grey.primary}
                              >
                                {t(`order.extra_size_breakdown`, {
                                  productName: t(`${item.productName}.name`),
                                  breakdown: extraSizeBreakdown,
                                })}
                              </Typography>
                            </CopyableBox>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="textSmBold">
                            {item.numberOfExtraSize}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="textSmBold">
                            {extraSizeUnitPrice &&
                              currencyFormatter?.format(extraSizeUnitPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="textSmBold">
                            {extraSizeTotal &&
                              currencyFormatter?.format(extraSizeTotal)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </Box>
          </FlexColBox>
          {shippingOptions && (
            <FlexColBox gap="8px">
              <CopyableBox
                value={shippingOptionsString}
                copyButtonProps={{
                  size: "medium",
                }}
                hideCopyButton={!isFromAdmin}
              >
                <Typography variant="textRegularBold">
                  {shippingOptionsString}
                </Typography>
              </CopyableBox>
              <CopyableBox
                value={shippingPriceString}
                hideCopyButton={!isFromAdmin}
              >
                <Typography
                  variant="textXs"
                  sx={{ color: palette.grey.primary }}
                  pt="2px"
                >
                  {shippingPriceString}
                </Typography>
              </CopyableBox>
            </FlexColBox>
          )}
          <OrderItemSizes
            sizes={item.sizes || []}
            onUpdateSizesQty={
              !onUpdateSizesQty
                ? undefined
                : (sizes) => {
                    return onUpdateSizesQty(item.designId, sizes);
                  }
            }
          />
        </FlexColBox>
      </Box>

      <DeleteModal
        open={showDeleteModal}
        onDelete={onClicRemovePurchasedItem}
        title={t("order.deleteConfirmation.title")}
        message={t("order.deleteConfirmation.message", {
          project_name: item.projectName,
          design_name: item.designName,
        })}
        onClose={onDeleteClickClose}
      />
    </Box>
  );
};

export default OrderItemDetails;
